import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { KPICard } from "../ui/kpi-card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Spinner } from "../ui/spinner";
import {
  Skeleton,
  SkeletonKPICard,
  SkeletonTable,
  SkeletonChart,
  SkeletonCard,
} from "../ui/skeleton";
import { Alert, AlertDescription } from "../ui/alert";
import {
  EnhancedTabs,
  EnhancedTabsContent,
  EnhancedTabsList,
  EnhancedTabsTrigger,
} from "../ui/enhanced-tabs";
import { Dialog, DialogContent } from "../ui/dialog";
import {
  CleanTable,
  CleanTableBody,
  CleanTableCell,
  CleanTableHead,
  CleanTableHeader,
  CleanTableRow,
} from "../ui/clean-table";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Building,
  Building2,
  AlertTriangle,
  BarChart3,
  Shield,
  Settings,
  Users,
} from "lucide-react";
import { useUser } from "../../contexts/UserContext";
import PageHeader from "../Common/PageHeader";

import { RiskService } from "../../services/riskService";
import { ControlService } from "../../services/controlService";
import { MetadataService } from "../../services/metadataService";
import { BusinessUnitService } from "../../services/businessUnitService";
import { getApiUrl } from "../../config/api";
import RiskHeatMap from "../Common/RiskHeatMap";
import RiskCategoryDistribution from "../Common/RiskCategoryDistribution";
import {
  calculateInherentRisk,
  calculateResidualRisk,
  calculateRiskCoverage,
  formatRiskValue,
} from "../../lib/riskAnalytics";

const OrganizationDashboard = () => {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { canManageOrganization } = useUser();

  const [organization, setOrganization] = useState(null);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [orgRisks, setOrgRisks] = useState([]);
  const [orgControls, setOrgControls] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [likelihoods, setLikelihoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [risksLoading, setRisksLoading] = useState(false);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [businessUnitsLoading, setBusinessUnitsLoading] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("business-units");

  const canManage = canManageOrganization(organizationId);

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        getApiUrl(`organizations/${organizationId}`),
        {
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch organization");
      }

      const data = await response.json();
      setOrganization(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessUnits = async () => {
    if (!organizationId) return;

    try {
      setBusinessUnitsLoading(true);
      const data =
        await BusinessUnitService.getBusinessUnitsForOrganizationSummary(
          organizationId,
          getAccessTokenSilently
        );
      setBusinessUnits(data);
    } catch (err) {
      console.error("Error fetching business units:", err);
      setBusinessUnits([]);
    } finally {
      setBusinessUnitsLoading(false);
    }
  };

  const fetchOrganizationRisks = async () => {
    if (!organizationId || !organization?.approved) return;

    try {
      setRisksLoading(true);
      const data = await RiskService.getOrganizationRisks(
        organizationId,
        getAccessTokenSilently
      );
      setOrgRisks(data);
    } catch (err) {
      console.error("Error fetching organization risks:", err);
      setOrgRisks([]);
    } finally {
      setRisksLoading(false);
    }
  };

  const fetchOrganizationControls = async () => {
    if (!organizationId || !organization?.approved) return;

    try {
      setControlsLoading(true);
      const data = await ControlService.getControls(
        organizationId,
        null,
        getAccessTokenSilently
      );
      setOrgControls(data);
    } catch (err) {
      console.error("Error fetching organization controls:", err);
      setOrgControls([]);
    } finally {
      setControlsLoading(false);
    }
  };

  const fetchMetadata = async () => {
    if (!organizationId || !organization?.approved) return;

    try {
      setMetadataLoading(true);
      const [severitiesData, likelihoodsData] = await Promise.all([
        MetadataService.getSeverities(organizationId, getAccessTokenSilently),
        MetadataService.getLikelihoods(organizationId, getAccessTokenSilently),
      ]);
      setSeverities(severitiesData);
      setLikelihoods(likelihoodsData);
    } catch (err) {
      console.error("Error fetching metadata:", err);
      setSeverities([]);
      setLikelihoods([]);
    } finally {
      setMetadataLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchOrganizationData();
    }
  }, [organizationId, getAccessTokenSilently]);

  useEffect(() => {
    if (organization) {
      fetchBusinessUnits();
      fetchOrganizationRisks();
      fetchOrganizationControls();
      fetchMetadata();
    }
  }, [organization, getAccessTokenSilently]);

  const handleViewBusinessUnit = (businessUnit) => {
    navigate(`/business-unit/${businessUnit.id}/dashboard`);
  };

  if (loading) {
    return (
      <div className="p-10 w-full">
        {/* Header Skeleton */}
        <div className="flex items-center gap-4 mb-8">
          <Skeleton className="h-8 w-64" />
        </div>

        {/* KPI Cards Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonKPICard key={index} />
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <SkeletonChart />
          <SkeletonChart />
        </div>

        {/* Tabs Skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <SkeletonCard>
            <div className="space-y-4">
              <Skeleton className="h-6 w-48" />
              <SkeletonTable rows={5} columns={4} />
            </div>
          </SkeletonCard>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-10">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="container mx-auto p-10">
        <Alert>
          <AlertDescription>Organization not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-10 w-full">
      {/* Header */}
      <PageHeader pageName="Dashboard" entityName={organization?.name} />

      {/* Content */}
      {!organization.approved ? (
        <Alert>
          <AlertDescription>
            This organization is pending approval. Features will be available
            once approved.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {/* KPI Cards - Two Rows */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <KPICard
              title="Units"
              value={businessUnits.length}
              icon={Building2}
              variant="horizontal"
              valueColor="text-foreground"
              className="border-2 border-slate-200"
            />
            <KPICard
              title="Total Risks"
              value={orgRisks.length}
              icon={AlertTriangle}
              valueColor="text-foreground"
              variant="horizontal"
              className="border-2 border-slate-200"
            />
            <KPICard
              title="Controls"
              value={orgControls.length}
              icon={Shield}
              valueColor="text-foreground"
              variant="horizontal"
              className="border-2 border-slate-200"
            />
            <KPICard
              title="Inherent"
              value={formatRiskValue(calculateInherentRisk(orgRisks))}
              icon={AlertTriangle}
              valueColor="text-foreground"
              variant="horizontal"
              className="border-2 border-slate-200"
            />
            <KPICard
              title="Residual"
              value={formatRiskValue(calculateResidualRisk(orgRisks))}
              icon={Shield}
              valueColor="text-foreground"
              variant="horizontal"
              className="border-2 border-slate-200"
            />
            <KPICard
              title="Coverage"
              value={`${calculateRiskCoverage(orgRisks)}%`}
              icon={BarChart3}
              valueColor="text-foreground"
              variant="horizontal"
              className="border-2 border-slate-200"
            />
          </div>

          {/* Risk Analytics - Moved here from overview tab */}
          {canManage && orgRisks.length > 0 && (
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              <RiskHeatMap
                risks={orgRisks}
                severities={severities}
                likelihoods={likelihoods}
                title="Risk Heat Map"
                className="border-2 border-slate-200"
              />
              <RiskCategoryDistribution
                risks={orgRisks}
                title="Risk Category Distribution"
                className="border-2 border-slate-200"
              />
            </div>
          )}

          {/* Loading state for analytics - Moved here from overview tab */}
          {canManage && (risksLoading || metadataLoading) && (
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              <SkeletonChart className="border-2 border-slate-200" />
              <SkeletonChart className="border-2 border-slate-200" />
            </div>
          )}

          {/* Tabs */}
          <EnhancedTabs value={activeTab} onValueChange={setActiveTab}>
            <EnhancedTabsList variant="compact">
              <EnhancedTabsTrigger value="business-units" icon={Building2}>
                Business Units
              </EnhancedTabsTrigger>
              <EnhancedTabsTrigger value="risks" icon={AlertTriangle}>
                Risks
              </EnhancedTabsTrigger>
              <EnhancedTabsTrigger value="controls" icon={Shield}>
                Controls
              </EnhancedTabsTrigger>
              {canManage && (
                <>
                  <EnhancedTabsTrigger value="metadata" icon={Settings}>
                    Metadata
                  </EnhancedTabsTrigger>
                </>
              )}
            </EnhancedTabsList>

            <EnhancedTabsContent value="business-units" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Business Units Overview</CardTitle>
                    {canManage && (
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/organization/${organizationId}/business-units`
                          )
                        }
                        className="flex items-center gap-2"
                      >
                        <Settings className="h-4 w-4" />
                        Manage All
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {businessUnitsLoading ? (
                    <div className="space-y-4">
                      <SkeletonTable rows={5} columns={4} />
                    </div>
                  ) : businessUnits.length === 0 ? (
                    <div className="text-center py-12">
                      <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        No business units have been created for this
                        organization yet.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Business Units Table */}
                      <div>
                        <TooltipProvider>
                          <CleanTable>
                            <CleanTableHeader>
                              <CleanTableRow>
                                <CleanTableHead>Business Unit</CleanTableHead>
                                <CleanTableHead>Unit Heads</CleanTableHead>
                                <CleanTableHead className="text-center">
                                  Total Risks
                                </CleanTableHead>
                                <CleanTableHead className="text-right">
                                  Dashboard
                                </CleanTableHead>
                              </CleanTableRow>
                            </CleanTableHeader>
                            <CleanTableBody>
                              {businessUnits.map((unit) => (
                                <CleanTableRow key={unit.id}>
                                  <CleanTableCell>
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {unit.name}
                                      </p>
                                    </div>
                                  </CleanTableCell>
                                  <CleanTableCell>
                                    <div className="flex items-center">
                                      {unit.heads && unit.heads.length > 0 ? (
                                        <div className="flex -space-x-2">
                                          {unit.heads.map((head, index) => (
                                            <Tooltip key={head.id || index}>
                                              <TooltipTrigger>
                                                <Avatar className="h-8 w-8 border-2 border-white hover:z-10 relative">
                                                  <AvatarImage
                                                    src={head.profile_pic}
                                                    alt={head.name}
                                                  />
                                                  <AvatarFallback className="text-xs">
                                                    {head.name
                                                      .split(" ")
                                                      .map((n) => n[0])
                                                      .join("")
                                                      .toUpperCase()}
                                                  </AvatarFallback>
                                                </Avatar>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>{head.name}</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          ))}
                                        </div>
                                      ) : (
                                        <span className="text-gray-500">
                                          No heads assigned
                                        </span>
                                      )}
                                    </div>
                                  </CleanTableCell>
                                  <CleanTableCell className="text-center">
                                    <span className="text-gray-900 font-medium">
                                      {unit.risk_count || 0}
                                    </span>
                                  </CleanTableCell>
                                  <CleanTableCell className="text-right">
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() =>
                                        handleViewBusinessUnit(unit)
                                      }
                                      className="p-0 h-auto"
                                    >
                                      View Dashboard →
                                    </Button>
                                  </CleanTableCell>
                                </CleanTableRow>
                              ))}
                            </CleanTableBody>
                          </CleanTable>
                        </TooltipProvider>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </EnhancedTabsContent>

            <EnhancedTabsContent value="risks" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Risks Overview</CardTitle>
                    {canManage && (
                      <Button
                        className="mt-4 flex items-center gap-2"
                        size="sm"
                        onClick={() =>
                          navigate(`/organization/${organizationId}/risks`)
                        }
                      >
                        <Settings className="h-4 w-4" />
                        Manage All
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {risksLoading ? (
                    <div className="space-y-4">
                      <SkeletonTable rows={5} columns={5} />
                    </div>
                  ) : orgRisks.length === 0 ? (
                    <div className="text-center py-12">
                      <AlertTriangle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        No risks have been created for this organization yet.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Risks Table */}
                      <div>
                        <CleanTable>
                          <CleanTableHeader>
                            <CleanTableRow>
                              <CleanTableHead>Risk ID</CleanTableHead>
                              <CleanTableHead>Risk Name</CleanTableHead>
                              <CleanTableHead>Category</CleanTableHead>
                              <CleanTableHead className="text-center">
                                Severity
                              </CleanTableHead>
                              <CleanTableHead className="text-center">
                                Likelihood
                              </CleanTableHead>
                            </CleanTableRow>
                          </CleanTableHeader>
                          <CleanTableBody>
                            {orgRisks.slice(0, 5).map((risk) => (
                              <CleanTableRow key={risk.id}>
                                <CleanTableCell>
                                  <span className="text-sm font-mono text-gray-600">
                                    {risk.custom_id || risk.id?.slice(0, 8)}
                                  </span>
                                </CleanTableCell>
                                <CleanTableCell>
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {risk.name ||
                                        risk.description ||
                                        "No name provided"}
                                    </p>
                                    {risk.description &&
                                      risk.name !== risk.description && (
                                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                          {risk.description}
                                        </p>
                                      )}
                                  </div>
                                </CleanTableCell>
                                <CleanTableCell>
                                  <span className="text-gray-700">
                                    {risk.Category?.name || "Uncategorized"}
                                  </span>
                                </CleanTableCell>
                                <CleanTableCell className="text-center">
                                  <span className="text-gray-900 font-medium">
                                    {risk.Severity?.value || "N/A"}
                                  </span>
                                </CleanTableCell>
                                <CleanTableCell className="text-center">
                                  <span className="text-gray-900 font-medium">
                                    {risk.Likelihood?.value || "N/A"}
                                  </span>
                                </CleanTableCell>
                              </CleanTableRow>
                            ))}
                          </CleanTableBody>
                        </CleanTable>
                        {orgRisks.length > 5 && (
                          <div className="px-4 py-4 text-center border-t border-gray-200">
                            <Button
                              size="sm"
                              onClick={() =>
                                navigate(
                                  `/organization/${organizationId}/risks`
                                )
                              }
                              className="flex items-center gap-2"
                            >
                              <Settings className="h-4 w-4" />
                              Manage All
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </EnhancedTabsContent>

            <EnhancedTabsContent value="controls" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Controls Overview</CardTitle>
                    {canManage && (
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(`/organization/${organizationId}/controls`)
                        }
                        className="flex items-center gap-2"
                      >
                        <Settings className="h-4 w-4" />
                        Manage All
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {controlsLoading ? (
                    <div className="space-y-4">
                      <SkeletonTable rows={5} columns={5} />
                    </div>
                  ) : orgControls.length === 0 ? (
                    <div className="text-center py-12">
                      <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        No controls have been created for this organization yet.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Controls Table */}
                      <div>
                        <CleanTable>
                          <CleanTableHeader>
                            <CleanTableRow>
                              <CleanTableHead>Control ID</CleanTableHead>
                              <CleanTableHead>Control Name</CleanTableHead>
                              <CleanTableHead>Category</CleanTableHead>
                              <CleanTableHead>Sub-Category</CleanTableHead>
                              <CleanTableHead className="text-center">
                                Assigned Users
                              </CleanTableHead>
                            </CleanTableRow>
                          </CleanTableHeader>
                          <CleanTableBody>
                            {orgControls.slice(0, 5).map((control) => (
                              <CleanTableRow key={control.id}>
                                <CleanTableCell>
                                  <span className="text-sm font-mono text-gray-600">
                                    {control.custom_id}
                                  </span>
                                </CleanTableCell>
                                <CleanTableCell>
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {control.name}
                                    </p>
                                  </div>
                                </CleanTableCell>
                                <CleanTableCell>
                                  <span className="text-gray-700">
                                    {control.SubCategory?.Category?.name ||
                                      control.categories?.[0]?.name ||
                                      control.Category?.name ||
                                      "Uncategorized"}
                                  </span>
                                </CleanTableCell>
                                <CleanTableCell>
                                  {control.SubCategory ? (
                                    <span className="text-gray-900">
                                      {control.SubCategory.name}
                                    </span>
                                  ) : (
                                    <span className="text-gray-500 text-sm">
                                      No sub-category
                                    </span>
                                  )}
                                </CleanTableCell>
                                <CleanTableCell className="text-center">
                                  <span className="text-gray-900 font-medium">
                                    {control.assigned_users?.length || 0}
                                  </span>
                                </CleanTableCell>
                              </CleanTableRow>
                            ))}
                          </CleanTableBody>
                        </CleanTable>
                        {orgControls.length > 5 && (
                          <div className="px-4 py-4 text-center border-t border-gray-200">
                            <Button
                              size="sm"
                              onClick={() =>
                                navigate(
                                  `/organization/${organizationId}/controls`
                                )
                              }
                              className="flex items-center gap-2"
                            >
                              <Settings className="h-4 w-4" />
                              Manage All
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </EnhancedTabsContent>

            <EnhancedTabsContent value="metadata" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Metadata Overview</CardTitle>
                    {canManage && (
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(`/organization/${organizationId}/settings`)
                        }
                        className="flex items-center gap-2"
                      >
                        <Settings className="h-4 w-4" />
                        Manage All
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {metadataLoading ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <SkeletonCard>
                        <div className="space-y-4">
                          <Skeleton className="h-6 w-32" />
                          {Array.from({ length: 4 }).map((_, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-3 p-3"
                            >
                              <Skeleton className="w-3 h-3 rounded-full" />
                              <Skeleton className="h-4 flex-1" />
                              <Skeleton className="h-5 w-16" />
                              <Skeleton className="h-4 w-20" />
                            </div>
                          ))}
                        </div>
                      </SkeletonCard>
                      <SkeletonCard>
                        <div className="space-y-4">
                          <Skeleton className="h-6 w-32" />
                          {Array.from({ length: 4 }).map((_, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-3 p-3"
                            >
                              <Skeleton className="w-3 h-3 rounded-full" />
                              <Skeleton className="h-4 flex-1" />
                              <Skeleton className="h-5 w-16" />
                              <Skeleton className="h-4 w-20" />
                            </div>
                          ))}
                        </div>
                      </SkeletonCard>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Severity Levels */}
                      <Card>
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between">
                            <CardTitle>Severity Levels</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          {severities.length === 0 ? (
                            <div className="text-center py-8">
                              <p className="text-gray-500 text-sm">
                                No severity levels defined
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {severities
                                .sort((a, b) => a.value - b.value)
                                .map((severity) => {
                                  const riskCount = orgRisks.filter(
                                    (risk) => risk.severity_id === severity.id
                                  ).length;

                                  // Color mapping based on severity level
                                  const getColorClass = (value) => {
                                    if (value <= 2) return "bg-green-500";
                                    if (value <= 4) return "bg-yellow-500";
                                    if (value <= 7) return "bg-orange-500";
                                    return "bg-red-500";
                                  };

                                  return (
                                    <div
                                      key={severity.id}
                                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                      <div
                                        className={`w-3 h-3 rounded-full ${getColorClass(
                                          severity.value
                                        )}`}
                                      />
                                      <span className="font-medium text-gray-900">
                                        {severity.name || severity.description}
                                      </span>
                                      <Badge
                                        variant="outline"
                                        className="text-xs font-normal"
                                      >
                                        Level {severity.value}
                                      </Badge>
                                      <div className="ml-auto text-right">
                                        <p className="font-semibold text-gray-900">
                                          {riskCount} risk
                                          {riskCount !== 1 ? "s" : ""}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Likelihood Levels */}
                      <Card>
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between">
                            <CardTitle>Likelihood Levels</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          {likelihoods.length === 0 ? (
                            <div className="text-center py-8">
                              <p className="text-gray-500 text-sm">
                                No likelihood levels defined
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {likelihoods
                                .sort((a, b) => a.value - b.value)
                                .map((likelihood) => {
                                  const riskCount = orgRisks.filter(
                                    (risk) =>
                                      risk.likelihood_id === likelihood.id
                                  ).length;

                                  // Color mapping based on likelihood level
                                  const getColorClass = (value) => {
                                    if (value <= 2) return "bg-green-500";
                                    if (value <= 4) return "bg-lime-500";
                                    if (value <= 7) return "bg-yellow-500";
                                    if (value <= 9) return "bg-orange-500";
                                    return "bg-red-500";
                                  };

                                  return (
                                    <div
                                      key={likelihood.id}
                                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                      <div
                                        className={`w-3 h-3 rounded-full ${getColorClass(
                                          likelihood.value
                                        )}`}
                                      />
                                      <span className="font-medium text-gray-900">
                                        {likelihood.description}
                                      </span>
                                      <Badge
                                        variant="outline"
                                        className="text-xs font-normal"
                                      >
                                        Level {likelihood.value}
                                      </Badge>
                                      <div className="ml-auto text-right">
                                        <p className="font-semibold text-gray-900">
                                          {riskCount} risk
                                          {riskCount !== 1 ? "s" : ""}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </CardContent>
              </Card>
            </EnhancedTabsContent>
          </EnhancedTabs>
        </>
      )}
    </div>
  );
};

export default OrganizationDashboard;
